import { useMemo, useState } from 'react';
import { useMatches } from '@/data/use-matches';
import { useParticipants } from '@/data/use-participants';
import { computePlaces, placeMap } from '@/engine';
import { SingleElimBracket } from '@/features/bracket-view/single-elim-bracket';
import { DoubleElimBracket } from '@/features/bracket-view/double-elim-bracket';
import { RoundRobinView } from '@/features/bracket-view/round-robin-view';
import { SwissView } from '@/features/bracket-view/swiss-view';
import { GroupsPlayoffView } from '@/features/bracket-view/groups-playoff-view';
import { MatchInputDialog } from '@/features/match-input/match-input-dialog';
import { StartTournamentButton } from './start-tournament-button';
import { SwissControls } from './swiss-controls';
import { GeneratePlayoffButton } from './generate-playoff-button';
import { LoadingState, ErrorState, EmptyState } from '@/components/design';
import { Network } from 'lucide-react';
import type { Match, Tournament } from '@/data/types';

interface Props {
  tournament: Tournament;
}

export function BracketTab({ tournament }: Props) {
  const { matches, error } = useMatches(tournament.id);
  const { participants } = useParticipants(tournament.id);
  const [selected, setSelected] = useState<Match | null>(null);

  const placeByParticipant = useMemo(
    () =>
      placeMap(
        computePlaces(
          matches ?? [],
          (participants ?? []).map((p) => p.id),
          tournament.format,
          tournament.groups
        )
      ),
    [matches, participants, tournament.format, tournament.groups]
  );

  if (error) return <ErrorState title="Не удалось загрузить сетку" body={error.message} />;
  if (matches === undefined) return <LoadingState />;

  if (matches.length === 0) {
    return (
      <div className="card-d" style={{ padding: 0 }}>
        <EmptyState
          icon={Network}
          title="Сетка ещё не сгенерирована"
          body="Завершите жеребьёвку, чтобы построить турнирную сетку. После этого матчи можно будет вводить."
          action={
            <StartTournamentButton tournament={tournament} participants={participants ?? []} />
          }
        />
      </div>
    );
  }

  const maxRound = Math.max(...matches.map((m) => m.round));

  const view = (() => {
    if (tournament.format === 'SINGLE_ELIM') {
      return (
        <SingleElimBracket
          matches={matches}
          rounds={maxRound}
          onSelectMatch={setSelected}
          placeByParticipant={placeByParticipant}
        />
      );
    }
    if (tournament.format === 'ROUND_ROBIN') {
      return (
        <RoundRobinView
          matches={matches}
          participants={participants ?? []}
          onSelectMatch={setSelected}
          placeByParticipant={placeByParticipant}
        />
      );
    }
    if (tournament.format === 'DOUBLE_ELIM') {
      return (
        <DoubleElimBracket
          matches={matches}
          onSelectMatch={setSelected}
          placeByParticipant={placeByParticipant}
        />
      );
    }
    if (tournament.format === 'SWISS') {
      return (
        <div className="space-y-6">
          <SwissControls tournament={tournament} matches={matches} />
          <SwissView
            matches={matches}
            participants={participants ?? []}
            onSelectMatch={setSelected}
            placeByParticipant={placeByParticipant}
          />
        </div>
      );
    }
    if (tournament.format === 'GROUPS_PLAYOFF') {
      return (
        <div className="space-y-6">
          <GeneratePlayoffButton tournament={tournament} matches={matches} />
          <GroupsPlayoffView
            tournament={tournament}
            matches={matches}
            participants={participants ?? []}
            onSelectMatch={setSelected}
            placeByParticipant={placeByParticipant}
          />
        </div>
      );
    }
    return (
      <p className="text-muted-foreground">
        Отображение для формата {tournament.format} появится в следующих фазах.
      </p>
    );
  })();

  return (
    <div className="space-y-4">
      {view}
      <MatchInputDialog
        tournamentId={tournament.id}
        match={
          selected && (selected.status === 'READY' || selected.status === 'COMPLETED')
            ? selected
            : null
        }
        matchFormat={tournament.matchFormat}
        scoreMode={tournament.scoreMode ?? 'SETS'}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
